import { ErrorHandler, Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { CatchErrorService } from "./catch.service";

@Injectable()
export class ErrorHandlerService implements ErrorHandler {
  private sentryHandler = Sentry.createErrorHandler();

  constructor(private readonly cx: CatchErrorService) {}

  handleError(error: unknown): void {
    // Catch Indexed Database error caused by IOS on tab awake.
    // Affects firebase sdk
    if (isIndexedDbError(error)) {
      if (!sessionStorage.getItem("indexedDbReloaded")) {
        this.cx.logMessage("IndexedDB failure detected, forced reload...");
        sessionStorage.setItem("indexedDbReloaded", "true");
        location.reload();
      } else {
        this.cx.logWarning(
          "IndexedDB failure detected, reload skipped to prevent loop"
        );
      }
      return;
    }

    // Pass everything else to Sentry
    this.sentryHandler.handleError(error);
  }
}

/**
 * Determines whether the given error object represents the IndexedDB
 * "UnknownError" that typically occurs when the connection to the IndexedDB
 * server is lost (commonly seen on iOS Safari when waking from sleep).
 *
 * This error is characterized by an object with:
 * - `name` property equal to "UnknownError"
 * - `message` property containing a case-insensitive substring "indexed database"
 *
 * Because this error is often a native browser error-like object (not a standard
 * Error instance), this function performs a structural check instead of relying
 * on instanceof checks.
 *
 * @param error - The error object to check.
 * @returns `true` if the error matches the IndexedDB UnknownError signature, otherwise `false`.
 */
export function isIndexedDbError(error: unknown): boolean {
  if (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    "message" in error
  ) {
    const err = error as { name: unknown; message: unknown };
    return (
      err.name === "UnknownError" &&
      typeof err.message === "string" &&
      err.message.toLowerCase().includes("indexed database")
    );
  }
  return false;
}
